import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { keepPreviousData } from '@tanstack/react-query'

import { useGetAvailability } from '../api/generated/endpoints/availability/availability'
import { useListRates } from '../api/generated/endpoints/rates/rates'
import { SaleChooser } from '../components/SaleChooser'
import { priceFor, pricesDiffer, saleFrom, saleParams } from '../lib/sale'
import { DayStrip } from '../components/DayStrip'
import { HourAccordion } from '../components/HourAccordion'
import { SlotSummary } from '../components/SlotSummary'
import { firstHourWithSpace, parseBookSearch } from '../lib/slots'
import { formatUYU } from '../lib/money'

export const Route = createFileRoute('/book')({
  // Public: browsing the calendar needs no account. Only the booking step does.
  validateSearch: parseBookSearch,
  component: BookPage,
})

function BookPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  // Not destructured: React Query v5 types the result as a discriminated union
  // on `status`, and destructuring splits it into variables TypeScript can no
  // longer narrow.
  // The price list first: it decides which grid to ask for. If it fails, the
  // page still works the old way -- the default line, one hour -- rather than
  // not at all.
  const rates = useListRates({ query: { staleTime: 60_000 } })
  const sale = rates.data ? saleFrom(search, rates.data.rates, rates.data.coaches ?? []) : undefined

  const availability = useGetAvailability(
    { date: search.date, ...saleParams(sale) },
    {
      query: {
        enabled: !rates.isPending,
        // Somebody else may book the same slot while this page sits open.
        staleTime: 30_000,
        refetchInterval: 60_000,
        refetchIntervalInBackground: false,
        // The one that matters most: the customer tabs away to their email for
        // the magic link and comes back.
        refetchOnWindowFocus: true,
        // Changing day keeps the old list on screen instead of collapsing to a
        // skeleton and throwing the scroll position away.
        placeholderData: keepPreviousData,
      },
    },
  )

  const [openHour, setOpenHour] = useState<string | null>(null)

  if (rates.isPending || availability.isPending) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">Reservar un simulador</h1>
        <div className="space-y-px overflow-hidden rounded-lg border border-slate-200">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="h-14 animate-pulse bg-slate-100" />
          ))}
        </div>
      </div>
    )
  }

  if (availability.isError) {
    return (
      <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="font-medium text-red-900">No se pudieron cargar los horarios.</p>
        <button
          onClick={() => void availability.refetch()}
          className="mt-3 rounded-md bg-slate-900 px-4 py-2 text-white"
        >
          Reintentar
        </button>
      </div>
    )
  }

  const data = availability.data
  const selectedBay = search.bay
  // A closed day has every slot technically free -- nothing is booked, because
  // nothing CAN be. Without this the picker would happily take a selection and
  // the booking would then be refused by the server, which is a worse way to
  // find out.
  const anyFree =
    !data.closed && data.starts.some((slot) => !slot.past && slot.free_bay_ids.length > 0)
  const initialOpenHour = search.start?.slice(0, 2) ?? firstHourWithSpace(data.starts, selectedBay)

  const chosenSlot = data.starts.find((slot) => slot.start === search.start)

  const selectStart = (start: string) =>
    // REPLACE, not push. Arrow-keying through the radio group fires onChange per
    // keypress; with push, one keyboard user would generate six history entries
    // and the back button would become useless.
    void navigate({ search: (prev) => ({ ...prev, start }), replace: true })

  return (
    <div className="space-y-6 pb-28 md:pb-0">
      <div>
        <h1 className="text-2xl font-semibold">Reservar un simulador</h1>
        <p className="mt-1 text-slate-600">
          {data.slot_minutes} minutos &middot; {pricesDiffer(data) ? 'desde ' : ''}
          {formatUYU(data.price_uyu_pesos)} &middot; abierto de {data.opens_at}&ndash;
          {data.closes_at}
        </p>
      </div>

      {rates.data && sale && (
        <SaleChooser
          catalog={rates.data}
          sale={sale}
          onChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, ...next }), replace: true })
          }
        />
      )}

      <DayStrip
        selected={search.date}
        horizonDays={data.booking_horizon_days}
        closedDates={data.closed_dates}
      />

      {/* Before the grid, not instead of it. A day that vanishes looks like a
          bug; a day that is visibly shut is an answer. */}
      {data.closed && (
        <div
          role="status"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
        >
          <p className="font-medium">El local está cerrado ese día.</p>
          {data.closed_note && <p className="mt-1 text-sm">{data.closed_note}</p>}
          <p className="mt-1 text-sm">Elegir otro día arriba.</p>
        </div>
      )}

      {data.bays.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {/* Default is "Any": with two identical simulators, choosing between
              them is a decision with no information behind it. */}
          {[{ id: undefined, name: 'Cualquiera' }, ...data.bays].map((bay) => (
            <button
              key={bay.id ?? 'any'}
              type="button"
              onClick={() =>
                void navigate({ search: (prev) => ({ ...prev, bay: bay.id }), replace: true })
              }
              className={[
                'min-h-11 rounded-full border px-4 text-sm',
                selectedBay === bay.id
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 bg-white hover:bg-slate-50',
              ].join(' ')}
            >
              {bay.name}
            </button>
          ))}
        </div>
      )}

      <div className="md:grid md:grid-cols-[1fr_15rem] md:items-start md:gap-6">
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-slate-500">Elegir un horario</h2>
          {anyFree ? (
            <HourAccordion
              // Remount on day or bay change so the open hour re-derives, rather
              // than syncing it with an effect.
              key={`${search.date}:${selectedBay ?? 'any'}`}
              starts={data.starts}
              slotMinutes={data.slot_minutes}
              bay={selectedBay}
              selected={search.start}
              openHour={openHour ?? initialOpenHour}
              onOpenHour={setOpenHour}
              onSelect={selectStart}
            />
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-center">
              <p className="font-medium">
                {data.closed ? 'El local está cerrado ese día.' : 'No queda nada libre ese día.'}
              </p>
              <p className="mt-1 text-sm text-slate-600">Probar con otro día arriba.</p>
            </div>
          )}
        </section>

        {/* Desktop: a sticky card beside the list. */}
        <aside className="hidden md:sticky md:top-6 md:block">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-medium text-slate-500">La reserva</h2>
            <SlotSummary
              date={search.date}
              start={search.start}
              slotMinutes={data.slot_minutes}
              priceUyuPesos={priceFor(data, chosenSlot, selectedBay)}
            >
              <ContinueLink search={search} />
            </SlotSummary>
          </div>
        </aside>
      </div>

      {/* Mobile: a sticky bar. Same component, different wrapper. */}
      {search.start && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:hidden">
          <div className="mx-auto flex max-w-3xl items-end justify-between gap-4">
            <SlotSummary
              date={search.date}
              start={search.start}
              slotMinutes={data.slot_minutes}
              priceUyuPesos={priceFor(data, chosenSlot, selectedBay)}
            />
            <ContinueLink search={search} />
          </div>
        </div>
      )}
    </div>
  )
}

function ContinueLink({ search }: { search: ReturnType<typeof Route.useSearch> }) {
  if (!search.start) return null
  return (
    <Link
      to="/checkout"
      search={{
        date: search.date,
        bay: search.bay,
        start: search.start,
        rate: search.rate,
        people: search.people,
        hours: search.hours,
        coach: search.coach,
      }}
      className="inline-flex min-h-12 items-center justify-center rounded-md bg-slate-900 px-6 text-white hover:bg-slate-700"
    >
      Continuar
    </Link>
  )
}
